/**
 * Lender API Service
 *
 * Secure API client for fetching member data through the VeraScore Platform API.
 * Uses cookie-based session authentication (set by /verascore/login).
 *
 * API Endpoints (proxied via /api/verascore/*):
 * - GET /lender/members - List all members shared with lender's org
 * - GET /lender/members/:memberId - Get specific member detail
 *
 * Auth Flow:
 * 1. User logs in via LoginApi (/verascore/login)
 * 2. Backend sets session cookie
 * 3. All API calls include credentials: 'include' to send cookies
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

// PRIV-20.3: Purpose header for PII/NPPI access audit trail
// Header name must be 'x-request-purpose' to match backend purposeValidationMiddleware
const PURPOSE_HEADER = 'x-request-purpose';

/**
 * PRIV-20.3: Purpose types for lender actions
 * - lender_review: Default browsing, no specific intent
 * - underwriting: Actively evaluating a loan application
 * - credit_check: Accessing credit report or credit-related data
 * - fraud_prevention: Investigating suspicious activity
 */
export type LenderPurpose = 'lender_review' | 'underwriting' | 'credit_check' | 'fraud_prevention';

// Default purpose for general member data access
const DEFAULT_PURPOSE: LenderPurpose = 'lender_review';

// ============================================================================
// Types (matching API response from lenderService.ts)
// ============================================================================

/**
 * CC-023B: Unresolved account detail in lender completeness payload.
 * All fields are redacted/masked/bucketed by the backend PII redaction layer
 * before reaching the frontend. Raw PII never crosses the API boundary
 * in masked/redacted modes.
 *
 * PII Exposure Levels (controlled by backend PII_EXPOSURE_LEVEL env var):
 * - full: raw values (requires explicit legal approval)
 * - masked: non-identifiable but useful (balance bucketed, creditor generalized)
 * - redacted: fully abstracted (all PII fields removed or generic)
 */
export interface LenderUnresolvedAccountDetail {
  account_ref: string;                       // Tokenized reference (opaque, deterministic)
  creditor_name: string;                     // Generalized or abstracted by backend
  account_mask: string | null;               // Masked ("····") or null
  account_type: string;                      // Preserved or abstracted
  bureau_balance: number | string | null;    // Raw number (full), bucketed string (masked), or "Balance present" / null (redacted)
  bureau_report_date: string | null;         // Quartered ("2026-Q1") or null
  status: string;
  issue_type?: string;                       // Semantic signal: "Unverified account" | "Expired connection" | "Pending review"
  severity?: 'high' | 'medium' | 'low';     // Semantic signal
  pii_exposure_level?: 'full' | 'masked' | 'redacted'; // Applied redaction level
}

/**
 * CC-023: Credit completeness payload for lender API response.
 * Null when no completeness snapshot exists (pre-framework member).
 */
export interface LenderCreditCompleteness {
  completeness_pct: number;
  known_accounts: number;
  verified_live_accounts: number;
  resolved_exception_accounts: number;
  unresolved_accounts: number;
  unresolved_details: LenderUnresolvedAccountDetail[];
  freshness_oldest_days: number | null;
  is_complete: boolean;
  is_submission_eligible: boolean;
  submission_mode: 'complete' | 'threshold_allowed' | 'exception_allowed' | 'blocked';
  data_quality_label: 'complete_fresh' | 'partial_fresh' | 'complete_stale' | 'partial_stale' | 'exception_only' | 'no_credit';
  score_confidence: 'high' | 'medium' | 'low' | 'unverified';
  show_estimated_qualifier: boolean;
  snapshot_date: string | null;
  // CC-210: Optional v2 multi-dimensional sibling payload.
  // Null when no v2 snapshot exists for this member (pre-framework or v2 read failed).
  completeness_v2?: LenderCompletenessV2 | null;
  // VS-CORE-002: Account-state confidence (additive to score_confidence).
  // Present when the confidence engine has run. Null for pre-framework members.
  account_state_confidence?: {
    confidence: 'HIGH' | 'MEDIUM' | 'LOW';
    confidence_score: number;
    connection_coverage_ratio: number;
    data_coverage_ratio: number;
    account_state_counts: Record<string, number>;
  } | null;
}

/**
 * CC-210: Credit mix breakdown — tradeline counts by category.
 * Null for legacy snapshots that predate the output_json.tradeline_counts field.
 */
export interface LenderCreditMix {
  revolving: number;
  installment: number;
  other: number;
  total: number;
}

/**
 * CC-210: V2 multi-dimensional completeness payload (matches backend
 * CompletenessV2Payload exactly). Additive to the v1 envelope.
 */
export interface LenderCompletenessV2 {
  snapshot_id: string | null;
  created_at: string | null;
  coverage_score: number | null;   // NULL when no matchable tradelines (e.g., installment-only)
  profile_score: number;
  overall_score: number;
  input_hash: string;
  model_version: string;
  reasons: Array<{ code: string; text: string; severity: string }>;
  credit_mix: LenderCreditMix | null;
}

// ============================================================================
// WP-8 Slice 8C (UI Display Data Contract) — additive state enums.
// Mirrors api-verascore-platform/api/src/services/lender/lenderService.ts and
// api/src/handlers/dashboardlender/lenderModels.ts. Lender payloads project
// only the externally-visible subset; internal-only states (evidence_only_*,
// shadow_proxy_*, test_cohort_*, provisional_*) are never emitted to the FE
// per ADR-WP8-001 §8 and Contract §20 and so are intentionally omitted here.
// ============================================================================

/**
 * Externally-visible subset of the canonical 13-state score taxonomy
 * (ADR-WP8-001 §5/§8). Used by /lender/members/:id/history per-row.
 */
export type LenderHistoryScoreState =
  | 'official_current'
  | 'official_historical'
  | 'insufficient_history'
  | 'legacy_unknown'
  | 'held_incomplete_data'
  | 'dedup_adjusted'
  | 'dedup_pending_evidence'
  | 'unavailable'
  | 'error';

/**
 * Rolling-window state enum (WP-6 Slice 6F / ADR-WP6-001). Used by
 * `*_score_state` fields on the per-member summary.
 */
export type LenderWindowState =
  | 'ok'
  | 'insufficient_data'
  | 'under_review'
  | 'held_incomplete_data'
  | 'policy_excluded';

/**
 * Per-factor state enum (WP-8 Slice 8A, Contract §13). `measured_zero` is the
 * only state for which the factor value MAY legitimately be 0; under any
 * non-`measured*` state the nullable companion MUST be null.
 */
export type LenderFactorState =
  | 'measured'
  | 'measured_zero'
  | 'not_applicable'
  | 'insufficient_data'
  | 'unavailable'
  | 'error';

export interface LenderMember {
  // Member identification
  member_id: string;
  auth0_user_id: string;
  org_id: string;
  member_name?: string; // Constructed from first_name + last_name (used in debug logs)

  // PII from Auth0
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zipcode?: string;

  // Consent info
  consent_status: string;
  consent_granted_at: string;
  consent_scopes?: string[];

  // VeraScore data
  // Phase 9G (BUG-SCORING-PATH-DIVERGENCE-01): Score fields are nullable.
  // After Phase 9D (official-only SQL view migration), members without an official
  // 4-criteria score will have null values here. This must NOT be displayed as 0.
  verascore: number | null;
  verascore_rating: string | null;
  score_month: string | null;

  // Factor scores (0-100) — null when no official score exists
  factor_dti: number | null;
  factor_liquid_reserves: number | null;
  factor_savings: number | null;
  factor_cash_flow: number | null;
  factor_debt: number | null;
  factor_payment_history: number | null;

  // Income
  yearly_gross_income: number;
  monthly_gross_income: number;

  // BUG-UI-062: Income basis metadata for transparency
  // Which income source was used for scoring
  income_basis_used?: 'ATTESTED' | 'DEPOSITED_NET' | 'VERIFIED_GROSS' | null;
  // Individual income source values for comparison
  attested_income?: number | null;
  deposited_income?: number | null;
  verified_income?: number | null;
  income_confidence?: number | null;

  // Historical scores — null when no official score history exists
  six_month_score: number | null;
  twelve_month_score: number | null;
  twenty_four_month_score: number | null;

  // Audit traceability
  run_id?: string; // Links to algo_runs table for score calculation audit

  // BUG-DATA-001/002: Payment history and DTI ratio (Jan 26, 2026)
  // From lender_member_summary view via algo_run_evidence LATERAL JOINs
  number_of_late_payments?: number | null;
  age_of_late_payments?: number | null;
  dti_ratio_value?: number | null;

  // CC-023: Credit completeness data for lender delivery
  // Null when no completeness snapshot exists (pre-framework member, backfill pending CC-029)
  credit_completeness?: LenderCreditCompleteness | null;

  // ==========================================================================
  // WP-8 Slice 8C — additive UI Display Data Contract fields.
  // All optional/default-undefined on the wire; consumers MUST tolerate
  // absence (Slice 8A producer projection is additive and per-handler).
  // ADR-WP8-001 §5/§6/§11/§13; UI-DISPLAY-DATA-CONTRACT §10/§11/§13/§18/§21.
  // ==========================================================================
  /** Canonical score state (lender-visible subset). Disambiguates null from 0. */
  score_state?: LenderHistoryScoreState;
  /** Null-vs-zero companion for the current verascore. Null when no official score. */
  verascore_nullable?: number | null;
  /** Lender-safe hold reason copy (Contract §11). Suppressed for internal-only holds. */
  hold_reason?: string | null;

  // Per-window state metadata (WP-6 Slice 6F, mirrored from producer).
  six_month_score_state?: LenderWindowState;
  twelve_month_score_state?: LenderWindowState;
  twenty_four_month_score_state?: LenderWindowState;

  // Per-factor state + nullable companions (WP-8 Slice 8A, Contract §13).
  factor_dti_state?: LenderFactorState;
  factor_liquid_reserves_state?: LenderFactorState;
  factor_savings_state?: LenderFactorState;
  factor_cash_flow_state?: LenderFactorState;
  factor_debt_state?: LenderFactorState;
  factor_payment_history_state?: LenderFactorState;
  factor_dti_nullable?: number | null;
  factor_liquid_reserves_nullable?: number | null;
  factor_savings_nullable?: number | null;
  factor_cash_flow_nullable?: number | null;
  factor_debt_nullable?: number | null;
  factor_payment_history_nullable?: number | null;
}

export interface LenderApiError {
  message: string;
  statusCode: number;
}

// ============================================================================
// API Functions
// ============================================================================

// Simple window-level cache for members data
// This persists across component remounts and potential module duplicates
interface GlobalMembersCache {
  members: LenderMember[] | null;
  timestamp: number;
}

declare global {
  interface Window {
    __lenderApiMembersCache?: GlobalMembersCache;
  }
}

const MEMBERS_CACHE_TTL = 30000; // 30 seconds cache

function getGlobalCache(): GlobalMembersCache {
  if (typeof window !== 'undefined') {
    if (!window.__lenderApiMembersCache) {
      window.__lenderApiMembersCache = {
        members: null,
        timestamp: 0
      };
    }
    return window.__lenderApiMembersCache;
  }
  return { members: null, timestamp: 0 };
}

/**
 * Clear the members cache - useful for debugging
 * Call this from browser console: window.clearLenderMembersCache()
 */
export function clearMembersCache(): void {
  const cache = getGlobalCache();
  cache.members = null;
  cache.timestamp = 0;
  if (import.meta.env.DEV) console.log('🗑️ [lenderApiService] Members cache cleared');
}

// Expose cache clearing function globally for debugging
if (typeof window !== 'undefined') {
  (window as any).clearLenderMembersCache = clearMembersCache;
}

/**
 * Fetch all members who have shared data with the lender's organization
 * Auth is handled via session cookie (credentials: 'include')
 *
 * SIMPLIFIED: Direct async function without IIFE wrapper to avoid Promise resolution issues
 *
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 */
export async function getMembers(purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<LenderMember[]> {
  const callId = Math.random().toString(36).substr(2, 6);
  const cache = getGlobalCache();
  const now = Date.now();

  if (import.meta.env.DEV) console.log(`📡 [lenderApiService][${callId}] getMembers called`);
  if (import.meta.env.DEV) console.log(`📡 [lenderApiService][${callId}] Cache:`, {
    hasMembers: !!cache.members,
    count: cache.members?.length ?? 0,
    age: cache.timestamp ? now - cache.timestamp : 'N/A'
  });

  // Return cached data if fresh
  if (cache.members && cache.members.length > 0 && (now - cache.timestamp) < MEMBERS_CACHE_TTL) {
    if (import.meta.env.DEV) console.log(`✅ [lenderApiService][${callId}] Returning ${cache.members.length} cached members (age: ${now - cache.timestamp}ms)`);
    return cache.members;
  }

  // Make the API call directly (no IIFE wrapper)
  if (import.meta.env.DEV) console.log(`📡 [lenderApiService][${callId}] Fetching from ${API_BASE}/verascore/lender/members`);

  try {
    const response = await fetch(`${API_BASE}/verascore/lender/members`, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
    });

    if (import.meta.env.DEV) console.log(`📡 [lenderApiService][${callId}] Response: ${response.status} ${response.ok ? 'OK' : 'FAIL'}`);

    // F-008: Differentiate 401 (session expired) from 403 (permission denied)
    if (response.status === 401) {
      if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService][${callId}] Session expired (401), redirecting to logout...`);
      cache.members = null;
      cache.timestamp = 0;
      window.history.replaceState({}, '', '?view=logout');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return [];
    }
    if (response.status === 403) {
      if (import.meta.env.DEV) console.warn(`🚫 [lenderApiService][${callId}] Permission denied (403) — user lacks required permissions for this endpoint`);
      // Do NOT log the user out — they are authenticated but lack permission for this endpoint.
      // Return empty array so the UI renders an empty state (not a broken page).
      return [];
    }

    if (!response.ok) {
      const errorText = await response.text();
      if (import.meta.env.DEV) console.error(`❌ [lenderApiService][${callId}] API error ${response.status}: ${errorText}`);
      // Return cached data on error if available
      if (cache.members && cache.members.length > 0) {
        if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService][${callId}] Returning ${cache.members.length} stale cached members due to error`);
        return cache.members;
      }
      return [];
    }

    const data = await response.json();
    const result: LenderMember[] = data?.members || data?.data?.members || [];

    if (import.meta.env.DEV) console.log(`✅ [lenderApiService][${callId}] Fetched ${result.length} members from API`);

    // DEBUG: Check if run_id is in API response
    const firstMember = result[0];
    if (firstMember) {
      if (import.meta.env.DEV) console.log(`🔍 [lenderApiService][${callId}] FIRST MEMBER run_id check:`, {
        name: firstMember.member_name,
        run_id: firstMember.run_id || 'MISSING',
        has_run_id: 'run_id' in firstMember,
      });
    }

    // Debug: Log Patricia Brown's scores
    const patriciaBrown = result.find(m => m.member_name === 'Patricia Brown');
    if (patriciaBrown) {
      if (import.meta.env.DEV) console.log(`🔍 [lenderApiService][${callId}] Patricia Brown API data:`, {
        verascore: patriciaBrown.verascore,
        six_month_score: patriciaBrown.six_month_score,
        twelve_month_score: patriciaBrown.twelve_month_score,
        twenty_four_month_score: patriciaBrown.twenty_four_month_score,
        run_id: patriciaBrown.run_id || 'MISSING',
      });
    }

    // Cache the result
    if (result.length > 0) {
      cache.members = result;
      cache.timestamp = Date.now();
      if (import.meta.env.DEV) console.log(`📡 [lenderApiService][${callId}] Cached ${result.length} members`);
    }

    return result;

  } catch (error) {
    if (import.meta.env.DEV) console.error(`❌ [lenderApiService][${callId}] Fetch error:`, error);

    // Return cached data on network error if available
    if (cache.members && cache.members.length > 0) {
      if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService][${callId}] Returning ${cache.members.length} stale cached members due to network error`);
      return cache.members;
    }

    // Return empty array instead of throwing (defensive)
    return [];
  }
}

/**
 * Fetch detailed information for a specific member
 * Auth is handled via session cookie (credentials: 'include')
 *
 * @param memberId - Member UUID
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 */
export async function getMemberDetail(memberId: string, purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<LenderMember> {
  if (import.meta.env.DEV) console.log(`📡 [lenderApiService] Fetching member detail: ${memberId}, purpose: ${purpose}`);

  const response = await fetch(`${API_BASE}/verascore/lender/members/${memberId}`, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
    },
    credentials: 'include', // Sends session cookie for auth
  });

  // F-008: Differentiate 401 (session expired) from 403 (permission denied)
  if (response.status === 401) {
    if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Session expired (401), redirecting to logout...`);
    window.history.replaceState({}, '', '?view=logout');
    window.dispatchEvent(new PopStateEvent('popstate'));
    throw new Error(`Session expired (401)`);
  }
  if (response.status === 403) {
    if (import.meta.env.DEV) console.warn(`🚫 [lenderApiService] Permission denied (403) — insufficient permissions for member detail`);
    throw new Error(`Permission denied: You do not have access to this member's details.`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    if (import.meta.env.DEV) console.error('❌ [lenderApiService] API error:', response.status, errorText);
    throw new Error(`API error ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  if (import.meta.env.DEV) console.log(`✅ [lenderApiService] Fetched member: ${data.first_name} ${data.last_name}`);

  return data;
}

/**
 * Financial inputs for Score Details expanded view
 */
export interface MemberHistoryInputs {
  yearly_gross_income: number | null;
  gross_income: number | null;
  net_savings: number | null;
  liquid_reserves: number | null;
  monthly_expenses: number | null;
  minimum_debt_payments: number | null;
  money_leftover: number | null;
  outstanding_debt: number | null;
  dti_ratio: number | null;
}

/**
 * Historical score data point
 */
export interface MemberHistoryPoint {
  month: string;
  score: number;
  rating: string;
  factors?: {
    cash_flow: number | null;
    debt_to_income: number | null;
    debt: number | null;
    savings: number | null;
    liquid_reserves: number | null;
    payment_history: number | null;
  };
  inputs?: MemberHistoryInputs;
  // WP-8 Slice 8C — additive per-row state + nullable companion (Slice 8A
  // producer). Optional for back-compat with pre-WP-8 history responses.
  // ADR-WP8-001 §5/§6, UI-DISPLAY-DATA-CONTRACT §12/§18.
  score_state?: LenderHistoryScoreState;
  score_nullable?: number | null;
  included_in_rolling_average?: boolean;
  // WP-8 Slice 8D — additive display posture marker emitted by the
  // producer alongside the canonical score_state. The FE uses it to
  // exclude legacy rows from the modal month selector and from the
  // /history.inputs fallback path. Lender-safe values observed in
  // production: 'current' | 'restated' | 'legacy'. Treated as opaque
  // here; only the literal 'legacy' is filtered.
  display_state?: string | null;
}

/**
 * API response for member history
 */
export interface MemberHistoryResponse {
  memberId: string;
  months: number;
  series: MemberHistoryPoint[];
  // WP-8 Slice 8C — when `series.length === 0`, producers annotate the empty
  // state so the FE can distinguish "no history exists" from "policy-hidden"
  // (Contract §12). Omitted when series is non-empty.
  empty_reason?: 'no_history' | 'hidden_by_policy';
}

/**
 * Fetch historical VeraScore data for a member
 * Auth is handled via session cookie (credentials: 'include')
 *
 * @param memberId - Member UUID
 * @param options - Query options (months, includeFactors, includeInputs, purpose)
 */
export async function getMemberHistory(
  memberId: string,
  options: { months?: number; includeFactors?: boolean; includeInputs?: boolean; purpose?: LenderPurpose } = {}
): Promise<MemberHistoryResponse> {
  const { months = 24, includeFactors = false, includeInputs = false, purpose = DEFAULT_PURPOSE } = options;

  if (import.meta.env.DEV) console.log(`📡 [lenderApiService] Fetching member history: ${memberId}, months=${months}, includeInputs=${includeInputs}, purpose=${purpose}`);

  // Build URL string directly (same pattern as getMembers) to ensure we go through the /api proxy
  const queryParams = new URLSearchParams();
  queryParams.set('months', months.toString());
  if (includeFactors) queryParams.set('includeFactors', 'true');
  if (includeInputs) queryParams.set('includeInputs', 'true');

  const url = `${API_BASE}/verascore/lender/members/${memberId}/history?${queryParams.toString()}`;
  if (import.meta.env.DEV) console.log(`📡 [lenderApiService] History URL: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
    },
    credentials: 'include',
  });

  // F-008: Differentiate 401 (session expired) from 403 (permission denied)
  if (response.status === 401) {
    if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Session expired (401), redirecting to logout...`);
    window.history.replaceState({}, '', '?view=logout');
    window.dispatchEvent(new PopStateEvent('popstate'));
    throw new Error(`Session expired (401)`);
  }
  if (response.status === 403) {
    if (import.meta.env.DEV) console.warn(`🚫 [lenderApiService] Permission denied (403) — insufficient permissions for member history`);
    throw new Error(`Permission denied: You do not have access to this member's history.`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    if (import.meta.env.DEV) console.error('❌ [lenderApiService] API error:', response.status, errorText);
    throw new Error(`API error ${response.status}: ${errorText}`);
  }

  const json = await response.json();
  // API returns { status: "success", data: { memberId, months, series } }
  // Extract the inner data object
  const data = json.data || json;
  if (import.meta.env.DEV) console.log(`✅ [lenderApiService] Fetched ${data.series?.length || 0} months of history`);

  return data;
}

/**
 * Helper to check if an error is an API error
 */
export function isApiError(error: unknown): error is LenderApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'statusCode' in error
  );
}

// ============================================================================
// Access Event Logging (Audit Trail)
// Epic 16 Sub-Phase 1E: UI Instrumentation for Audit Framework
// ============================================================================

export type AccessEventType = 'VIEW_SCORE' | 'VIEW_FULL_PROFILE' | 'VIEW_AUDIT_PACK' | 'DOWNLOAD_REPORT';

export interface AccessEventRequest {
  member_id: string;
  access_type: AccessEventType;
  run_id?: string;
  fields_accessed?: string[];
  artifact_id?: string;
}

export interface AccessEventResponse {
  success: boolean;
  access_event_id: string;
  logged_at: string;
}

/**
 * Log a lender access event for audit trail
 * Called when lender views member data, expands score details, or downloads reports
 *
 * @param event - Access event details
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 * @returns Promise<AccessEventResponse> - Logged event details
 */
export async function logAccessEvent(event: AccessEventRequest, purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<AccessEventResponse | null> {
  if (import.meta.env.DEV) console.log(`📝 [lenderApiService] Logging access event:`, event, `purpose: ${purpose}`);

  try {
    const response = await fetch(`${API_BASE}/verascore/lender/access-events`, {
      method: 'POST',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
      body: JSON.stringify(event),
    });

    // Don't redirect on auth errors for logging - just fail silently
    if (!response.ok) {
      if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Failed to log access event: ${response.status}`);
      return null;
    }

    const data = await response.json();
    const result = data?.data || data;
    if (import.meta.env.DEV) console.log(`✅ [lenderApiService] Access event logged: ${result.access_event_id}`);
    return result;
  } catch (error) {
    // Fail silently - don't break the UI for audit logging failures
    if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Error logging access event:`, error);
    return null;
  }
}

// ============================================================================
// Contract Catalog (LAPI-017 / P4.2)
// ============================================================================

/**
 * Field entry from the contract catalog
 */
export interface ContractField {
  field_name: string;
  tier: number;
  pii_level: string;
  redaction_rule: string;
  consent_scope: string;
  description: string;
}

/**
 * Contract catalog response from GET /lender/contract
 */
export interface ContractCatalogResponse {
  contract: {
    version_id: string;
    status: string;
    effective_date: string;
    sunset_date: string | null;
    changelog: string | null;
  };
  fields: ContractField[];
  tiers: {
    tier_1_approved: string[];
    tier_2_conditional: string[];
    tier_3_restricted: string[];
  };
  meta: {
    total_fields: number;
    timestamp: string;
  };
}

/**
 * Fetch the active contract catalog — field registry, tier breakdown, version info.
 * This is a metadata-only endpoint (no PII, no member data).
 *
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail
 */
export async function getContract(purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<ContractCatalogResponse | null> {
  if (import.meta.env.DEV) console.log(`📜 [lenderApiService] Fetching contract catalog, purpose: ${purpose}`);

  try {
    const response = await fetch(`${API_BASE}/verascore/lender/contract`, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: purpose,
      },
      credentials: 'include',
    });

    // F-008: Differentiate 401 (session expired) from 403 (permission denied)
    if (response.status === 401) {
      if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Session expired (401), redirecting to logout...`);
      window.history.replaceState({}, '', '?view=logout');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return null;
    }
    if (response.status === 403) {
      if (import.meta.env.DEV) console.warn(`🚫 [lenderApiService] Permission denied (403) — insufficient permissions for contract catalog`);
      return null;
    }

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Failed to fetch contract: ${response.status}`);
      return null;
    }

    const json = await response.json();
    const result = json?.data || json;
    if (import.meta.env.DEV) console.log(`✅ [lenderApiService] Contract catalog: v${result.contract?.version_id}, ${result.meta?.total_fields} fields`);
    return result;
  } catch (error) {
    if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Error fetching contract:`, error);
    return null;
  }
}

// ============================================================================
// Mapper Functions (Convert API response to app types)
// ============================================================================

import { v4 as uuidv4 } from 'uuid';
import type { Member } from '../types/member';
import type { FinancialHealthStats } from '../types/financialStats';

/**
 * Determine the appropriate DashboardStatus based on member data.
 * Maps consent_status and score data to UI-friendly status values.
 *
 * Phase 9G (BUG-SCORING-PATH-DIVERGENCE-01): Distinguishes "Awaiting Score"
 * (consented member, no official score yet) from "In Progress" (onboarding).
 * A null/0 verascore for a consented member means the official score hasn't
 * been published yet — it does NOT mean the member is still onboarding.
 */
function determineDashboardStatus(lm: LenderMember): string {
  // If consent not active, show appropriate status
  if (!lm.consent_status || lm.consent_status.toLowerCase() !== 'active') {
    const status = (lm.consent_status || '').toLowerCase();
    if (status === 'pending') return 'Pending';
    if (status === 'invited') return 'Invited';
    if (status === 'revoked') return 'Needs Attention';
    if (status === 'in_progress' || status === 'in progress') return 'In Progress';
    return 'Awaiting Dashboard';
  }

  // Phase 9G: Consented member with no official score.
  // This is NOT the same as onboarding — the member has consented and may have
  // data, but no 4-criteria official score has been published yet.
  if (lm.verascore === null || lm.verascore === undefined || lm.verascore === 0) {
    return 'Awaiting Score';
  }

  // If VeraScore is below threshold (40), needs attention
  if (lm.verascore < 40) {
    return 'Needs Attention';
  }

  // Otherwise, dashboard is ready
  return 'Dashboard Ready';
}

/**
 * Convert LenderMember from API to app's Member type
 */
export function mapLenderMemberToMember(lm: LenderMember): Member {
  return {
    uuid: lm.member_id || `temp-${Date.now()}-${uuidv4()}`,
    member_name: `${lm.first_name} ${lm.last_name}`.trim(),
    first_name: lm.first_name || '',
    last_name: lm.last_name || '',
    email: lm.email || '',
    photo: null,
    zipcode: lm.zipcode || '',
    gender: '',
    ethnicity: '',
    age: 0,
    date_of_birth: '',
    street_address: lm.street_address || '',
    apartment: null,
    city: lm.city || '',
    state: lm.state || '',
    yearly_gross_income: lm.yearly_gross_income || 0,
    inst_member_since: '',
    dashboard_status: determineDashboardStatus(lm),
    inst_loan_advisor: '',
    phone_number: lm.phone_number || '',
    run_id: lm.run_id, // Audit traceability
    // CC-024: Pass through credit completeness data for badge rendering
    credit_completeness: lm.credit_completeness ?? null,
  };
}

/**
 * Convert LenderMember financial data to FinancialHealthStats
 *
 * Note: score_month should always be present. If missing, logs a warning
 * but falls back to current month to avoid breaking the UI.
 * The generateHistoricalFinancialData() function should filter out
 * members without score_month before calling this function.
 */
export function mapLenderMemberToFinancialStats(lm: LenderMember): FinancialHealthStats {
  // Log warning if score_month is missing (should be caught earlier)
  if (!lm.score_month) {
    if (import.meta.env.DEV) console.warn(`⚠️ [mapLenderMemberToFinancialStats] Missing score_month for member ${lm.member_name || lm.member_id}, falling back to current month`);
  }

  return {
    month: lm.score_month || new Date().toISOString().slice(0, 7),
    verascore: lm.verascore,
    verascore_health: lm.verascore_rating,
    factor_dti: lm.factor_dti,
    factor_cash_on_hand: lm.factor_liquid_reserves,
    factor_savings: lm.factor_savings,
    factor_spending: lm.factor_cash_flow,
    factor_debt: lm.factor_debt,
    factor_payment_history: lm.factor_payment_history,
    yearly_gross_income: lm.yearly_gross_income,
    gross_income: lm.monthly_gross_income,
  };
}

/**
 * Generate minimal financial data from a LenderMember for initial display
 * Returns only current month data from the summary API
 *
 * NOTE: This does NOT include input fields (money_leftover, dti_ratio, etc.)
 * Those are only available via getMemberHistory() API with includeInputs=true
 *
 * For charts that need input fields, MemberCard will lazy-load complete history
 * when the card is expanded.
 *
 * BUG FIX: Only generate financial data if score_month is present.
 * If score_month is missing, return empty array to avoid creating entries
 * with the current calendar month (which may not have actual data).
 */
export function generateHistoricalFinancialData(lm: LenderMember): FinancialHealthStats[] {
  // Return only current month data - no synthetic historical data
  // MemberCard will fetch complete 24-month history when expanded

  // BUG FIX: Don't generate data if score_month is missing
  // This prevents polluting availableMonths with current calendar month
  // when no actual financial data exists for that month
  if (!lm.score_month) {
    if (import.meta.env.DEV) console.warn(`⚠️ [generateHistoricalFinancialData] No score_month for member ${lm.member_name || lm.member_id}, skipping financial data generation`);
    return [];
  }

  return [mapLenderMemberToFinancialStats(lm)];
}

/**
 * Convert LenderMember to MemberWithScore (combined type used by UI)
 */
export interface MemberWithScore extends Member {
  // Phase 9G: nullable — null means no official score exists
  currentScore?: number | null;
  financialData?: FinancialHealthStats[];
  // Historical score averages for trend calculation
  six_month_score?: number | null;
  twelve_month_score?: number | null;
  twenty_four_month_score?: number | null;
  verascore_rating?: string | null;
  // Audit traceability - inherited from Member but explicitly noted here
  run_id?: string;
  // BUG-UI-062: Income basis metadata for transparency
  income_basis_used?: 'ATTESTED' | 'DEPOSITED_NET' | 'VERIFIED_GROSS' | null;
  attested_income?: number | null;
  deposited_income?: number | null;
  verified_income?: number | null;
  income_confidence?: number | null;
}

export function mapLenderMemberToMemberWithScore(lm: LenderMember): MemberWithScore {
  // DEBUG: Check run_id at mapping entry
  if (import.meta.env.DEV) console.log(`🔍 [mapLenderMemberToMemberWithScore] INPUT lm.run_id for ${lm.member_name}: "${lm.run_id || 'MISSING'}"`);

  const member = mapLenderMemberToMember(lm);

  // DEBUG: Check if mapLenderMemberToMember preserved run_id
  if (import.meta.env.DEV) console.log(`🔍 [mapLenderMemberToMemberWithScore] AFTER mapLenderMemberToMember, member.run_id: "${member.run_id || 'MISSING'}"`);

  const historicalData = generateHistoricalFinancialData(lm);

  if (import.meta.env.DEV) console.log(`📊 [mapLenderMemberToMemberWithScore] Generated ${historicalData.length} months of data for ${lm.member_name}`);
  if (import.meta.env.DEV) console.log(`   Scores: ${historicalData.map(d => `${d.month}=${d.verascore}`).join(', ')}`);

  // Debug: Log the raw verascore value
  if (lm.member_name && lm.member_name.includes('Patricia Brown')) {
    if (import.meta.env.DEV) console.log(`🔍 [mapLenderMemberToMemberWithScore] Patricia Brown lm.verascore:`, {
      value: lm.verascore,
      type: typeof lm.verascore,
      stringified: JSON.stringify(lm.verascore),
    });
  }

  // WP-8 Slice 8C: prefer the additive nullable companion when the producer
  // emits it (Slice 8A). Falls back to the legacy `verascore` field for
  // back-compat with pre-WP-8 responses. Never coerce null → 0 here.
  const currentScoreResolved =
    lm.verascore_nullable !== undefined ? lm.verascore_nullable : lm.verascore;

  const result = {
    ...member,
    currentScore: currentScoreResolved,
    verascore: currentScoreResolved, // Add verascore field for MemberCard component
    financialData: historicalData,
    six_month_score: lm.six_month_score,
    twelve_month_score: lm.twelve_month_score,
    twenty_four_month_score: lm.twenty_four_month_score,
    // WP-8 Slice 8C.1 — propagate additive rolling-window state companions
    // so MemberCard tiles can surface lender-safe labels via
    // selectRollingWindowDisplay instead of generic "?" placeholders.
    six_month_score_state: lm.six_month_score_state,
    twelve_month_score_state: lm.twelve_month_score_state,
    twenty_four_month_score_state: lm.twenty_four_month_score_state,
    verascore_rating: lm.verascore_rating,
    run_id: lm.run_id, // Audit traceability
    // BUG-UI-062: Income basis metadata for transparency
    income_basis_used: lm.income_basis_used,
    attested_income: lm.attested_income,
    deposited_income: lm.deposited_income,
    verified_income: lm.verified_income,
    income_confidence: lm.income_confidence,
    // BUG-DATA-001/002: Payment history and DTI ratio (Jan 26, 2026)
    number_of_late_payments: lm.number_of_late_payments,
    age_of_late_payments: lm.age_of_late_payments,
    dti_ratio_value: lm.dti_ratio_value,
    // CC-024: Pass through credit completeness data for badge rendering
    credit_completeness: lm.credit_completeness ?? null,
  };

  // Debug: Log the result currentScore
  if (lm.member_name && lm.member_name.includes('Patricia Brown')) {
    if (import.meta.env.DEV) console.log(`🔍 [mapLenderMemberToMemberWithScore] Patricia Brown result.currentScore:`, {
      value: result.currentScore,
      type: typeof result.currentScore,
      stringified: JSON.stringify(result.currentScore),
    });
  }

  return result;
}

// ============================================================================
// Metric Registry (Canonical Labels)
// Epic 16 Sub-Phase 1E: UI Instrumentation for Audit Framework
// ============================================================================

export interface MetricRegistryEntity {
  entity_name: string;
  entity_type: 'METRIC' | 'FACTOR' | 'FEATURE';
  display_name: string;
  description: string;
  formula: string | null;
  period: string | null;
  unit: string | null;
  evidence_level: string;
  ui_label: string | null;
  ui_section: string | null;
}

export interface MetricRegistryResponse {
  factors: MetricRegistryEntity[];
  metrics: MetricRegistryEntity[];
  features: MetricRegistryEntity[];
  _meta: {
    total: number;
    fetched_at: string;
  };
}

/**
 * Fetch metric registry for canonical labels
 * This provides a single source of truth for factor/metric/feature names
 *
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 * @returns Promise<MetricRegistryResponse | null>
 */
export async function getMetricRegistry(purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<MetricRegistryResponse | null> {
  if (import.meta.env.DEV) console.log(`📚 [lenderApiService] Fetching metric registry, purpose: ${purpose}`);

  try {
    const response = await fetch(`${API_BASE}/verascore/metric-registry`, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Failed to fetch metric registry: ${response.status}`);
      return null;
    }

    const data = await response.json();
    const result = data?.data || data;
    if (import.meta.env.DEV) console.log(`✅ [lenderApiService] Fetched metric registry: ${result._meta?.total} entities`);
    return result;
  } catch (error) {
    if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Error fetching metric registry:`, error);
    return null;
  }
}

// ============================================================================
// Submission History (IMP-LCU-003)
// ============================================================================

/**
 * Submission history record from member_org_submissions table
 * IMP-LCU-003: For displaying in Evidence Modal Sources tab
 * IMP-LCU-003-v2: Added consentScopes, removed ipAddress/method from display
 */
export interface SubmissionHistoryItem {
  submissionId: string;
  submittedAt: string;
  verascore: number | null;
  yearAndMonth: string | null;
  consentScopes: string[] | null;
  // Kept for API compatibility but not displayed
  method: string;
  ipAddress: string | null;
}

/**
 * Submission history response from API
 */
export interface SubmissionHistoryResponse {
  memberId: string;
  orgId: string;
  totalSubmissions: number;
  submissions: SubmissionHistoryItem[];
}

/**
 * Fetch submission history for a member
 * IMP-LCU-003: Used by Evidence Modal Sources tab to show submission audit trail
 *
 * @param memberId - Member UUID
 * @param limit - Number of submissions to return (default: 3)
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 * @returns Submission history or null if not found
 */
export async function getSubmissionHistory(
  memberId: string,
  limit: number = 3,
  purpose: LenderPurpose = DEFAULT_PURPOSE
): Promise<SubmissionHistoryResponse | null> {
  if (import.meta.env.DEV) console.log(`🔍 [lenderApiService] Fetching submission history for member ${memberId}, purpose: ${purpose}`);

  try {
    // Use template string (not new URL) since API_BASE may be relative path like '/api'
    const url = `${API_BASE}/verascore/lender/members/${memberId}/submission-history?limit=${limit}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 404) {
        if (import.meta.env.DEV) console.log(`📭 [lenderApiService] No submission history for member ${memberId}`);
        return null;
      }
      if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Failed to fetch submission history: ${response.status}`);
      return null;
    }

    const data = await response.json();
    const result = data?.data || data;
    if (import.meta.env.DEV) console.log(`✅ [lenderApiService] Fetched ${result.submissions?.length || 0} submissions (total: ${result.totalSubmissions})`);
    return result;
  } catch (error) {
    if (import.meta.env.DEV) console.warn(`⚠️ [lenderApiService] Error fetching submission history:`, error);
    return null;
  }
}

